<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { ScatterChartSpec } from "../sbi/protocol";
import { baseOption, tooltipDefaults } from "./echarts-base";
import { autoYDomainFor } from "./chart-yscale.ts";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: ScatterChartSpec;
    /** Field → format registry; both x and y axes can fall back. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

// `spec.data` is a `ChartData` union (rows-array | SqlMarker |
// DeriveMarker). The InsightChart dispatcher only mounts this
// subcomponent after the marker has resolved — narrow with
// `Array.isArray` so the rest of the component can iterate without
// repeated unions, and so a defective dispatch path can't crash render.
const rows = computed(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const groups = computed(() => {
  const colorField = props.spec.color;
  if (!colorField) {
    return [{ name: props.spec.y, points: rows.value }];
  }
  const buckets = new Map<string, (typeof rows.value)[number][]>();
  for (const row of rows.value) {
    const key = String(row[colorField] ?? "—");
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(row);
  }
  return Array.from(buckets.entries()).map(([name, points]) => ({ name, points }));
});

const option = computed(() => {
  const base = baseOption({
    title: props.spec.title,
    // Per-axis `xFormat` / `yFormat` → per-call `format` (DefaultOpts)
    // → registry format for the axis field. The per-call default lands
    // on both axes; pass per-axis formats when they need to differ.
    xFormat: props.spec.xFormat ?? props.spec.format ?? props.formats[props.spec.x],
    yFormat: props.spec.yFormat ?? props.spec.format ?? props.formats[props.spec.y],
    legendNames: groups.value.length > 1 ? groups.value.map((g) => g.name) : [],
  }, ctx.value);
  // Bubble cap scales with the plot: 40px bubbles on a ~250px phone
  // plot overlap into one blob. Unmeasured / wide keeps 40.
  const w = ctx.value.width;
  const maxSymbol = w > 0 ? Math.min(40, Math.max(12, (w - 72) / 14)) : 40;
  return {
    ...base,
    xAxis: {
      ...base.xAxis,
      type: "value",
      // A narrow value x-axis gets fewer ticks and drops overlaps.
      ...(w > 0 && w < 480 ? { splitNumber: 3 } : {}),
      axisLabel: { ...base.xAxis.axisLabel, hideOverlap: true },
    },
    yAxis: {
      ...base.yAxis,
      type: "value" as const,
      ...(autoYDomainFor(
        props.spec.yScale,
        rows.value.map((row) => row[props.spec.y]),
      ) ?? {}),
    },
    tooltip: tooltipDefaults("item", ctx.value),
    series: groups.value.map((g) => ({
      name: g.name,
      type: "scatter",
      data: g.points.map((row) => {
        const x = row[props.spec.x];
        const y = row[props.spec.y];
        const sizeField = props.spec.size;
        const size = sizeField ? Number(row[sizeField]) : undefined;
        return size !== undefined && Number.isFinite(size) ? [x, y, size] : [x, y];
      }),
      symbolSize: props.spec.size
        ? (data: unknown) => {
            const arr = data as [unknown, unknown, number];
            const v = Number(arr[2]);
            return Number.isFinite(v) ? Math.max(4, Math.min(maxSymbol, Math.sqrt(v) * 2)) : 8;
          }
        : 8,
    })),
    legend: groups.value.length > 1 ? base.legend : { show: false },
  };
});

const height = computed(() =>
  resolveChartHeight("scatter", props.spec.height),
);

// Resolve an ECharts click params object back to the row that
// produced the clicked point. Series are produced by `groups.value`,
// so `seriesIndex` selects the bucket and `dataIndex` selects the
// point inside it. When `spec.color` is set we also annotate the
// datum with `_series` so the author can branch on which colour
// bucket was clicked.
// ECharts hands the @click handler a wide, partly-optional event
// shape; narrow at runtime rather than at the type level so the
// component-level @click assignment stays compatible (mirrors the
// bar / line chart pattern).
function onClick(p: { dataIndex: number }) {
  if (!props.spec.id || !p || typeof p.dataIndex !== "number") return;
  const params = p as { seriesIndex?: number; dataIndex: number; seriesName?: string };
  const seriesIndex = params.seriesIndex;
  if (typeof seriesIndex !== "number") return;
  const bucket = groups.value[seriesIndex];
  if (!bucket) return;
  const points = bucket.points as ReadonlyArray<Record<string, unknown>>;
  const row = points[params.dataIndex];
  if (!row) return;
  const datum: Record<string, unknown> = props.spec.color
    ? { ...row, _series: params.seriesName ?? bucket.name }
    : { ...row };
  emit("click-target", props.spec.id, datum);
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
